import { FC, useEffect, useState } from 'react'
import { Box, CircularProgress, IconButton, Stack } from '@mui/material'
import { OnyxiaTooltip } from 'ui/components/tooltip'
import FrameworkRemovedTooltip from '../framework-removed-tooltip/FrameworkRemovedTooltip'
import CloseIcon from '@mui/icons-material/Close'
import { ReportTemplateResponse, ValidateTemplateConfigResponse, ValidationErrorType } from 'models'
import { CpiServiceV3, FrameworkServiceV3 } from 'services'
import { useUsersLookup } from 'hooks/useUsersLookup'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import {
  CubeTransparentIcon,
  Squares2X2Icon,
  ChartBarIcon,
  TagIcon,
  ClockIcon,
  ArrowPathIcon,
  CalendarIcon,
  UsersIcon
} from '@heroicons/react/24/outline'
import { formatScheduleDate, formatScheduleFrequency } from '../../reports-v2.utils'
import theme from 'theme'
import {
  StyledDrawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  SectionHeading,
  InfoRow,
  InfoIconWrapper,
  InfoLabel,
  InfoValue,
  SectionDivider
} from './viewTemplateDrawer.styles'

interface ViewTemplateDrawerProps {
  isOpen: boolean
  onClose: () => void
  template: ReportTemplateResponse | null
  frameworkData: { name: string; categories: any[] } | null
  validationResult?: ValidateTemplateConfigResponse
  onFrameworkChange?: () => Promise<void>
}

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}

interface ScopeItem {
  id: string
  name: string
}

interface ScopeDisplayData {
  frameworkName: string
  categories: ScopeItem[]
  cpis: ScopeItem[]
}

const NO_RECIPIENTS: string[] = []

const ViewTemplateDrawer: FC<ViewTemplateDrawerProps> = ({ isOpen, onClose, template, frameworkData, validationResult, onFrameworkChange }) => {
  const [scopeData, setScopeData] = useState<ScopeDisplayData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const recipientIds = isOpen ? template?.schedule?.recipients ?? NO_RECIPIENTS : NO_RECIPIENTS
  const { getUser, getUserLabel } = useUsersLookup(recipientIds)

  const invalidItemMap = new Map<string, string>()
  validationResult?.errors?.forEach((e) => {
    if (e.type === ValidationErrorType.CATEGORY || e.type === ValidationErrorType.METRIC) {
      invalidItemMap.set(e.id, e.message)
    }
  })
  const hasFrameworkError = validationResult?.errors?.some((e) => e.type === ValidationErrorType.FRAMEWORK) ?? false

  useEffect(() => {
    if (!isOpen || !template?.config) {
      setScopeData(null)
      return
    }

    const fetchScopeData = async () => {
      try {
        setIsLoading(true)
        const config = template.config

        const isStandardFramework = ['nist', 'onyxia'].includes(config.framework)
        let frameworkName: string = config.framework || '-'
        if (hasFrameworkError) {
          frameworkName = '[removed]'
        } else if (!isStandardFramework && config.framework) {
          try {
            const customFramework = await FrameworkServiceV3.getCustomFramework()
            frameworkName = customFramework.title || '[removed]'
          } catch {
            frameworkName = frameworkData?.name || '[removed]'
          }
        }

        // Fetch categories from the template's saved framework for correct titles
        let fwCategories: any[] = []
        try {
          fwCategories = await FrameworkServiceV3.searchFrameworkCategories({
            framework: config.framework
          })
        } catch {
          fwCategories = frameworkData?.categories || []
        }

        let categories: ScopeItem[] = []
        let cpis: ScopeItem[] = []

        if (config.categories && config.categories.length > 0) {
          categories = config.categories
            .filter((cat) => cat.metrics.length === 0)
            .map((cat) => {
              const found = fwCategories.find((c: any) => c.id === cat.id)
              return { id: cat.id, name: found?.title || '[removed]' }
            })

          const allCpiMetrics: string[] = []
          config.categories.forEach((cat) => {
            if (cat.metrics.length > 0) {
              allCpiMetrics.push(...cat.metrics)
            }
          })

          if (allCpiMetrics.length > 0) {
            const results = await Promise.allSettled(
              allCpiMetrics.map((id) => CpiServiceV3.getCpiDetails(id))
            )
            cpis = results.map((result, idx) => {
              const metricId = allCpiMetrics[idx]
              if (result.status === 'fulfilled') {
                const cpi = result.value
                return { id: metricId, name: cpi.display_name || metricId }
              }
              return { id: metricId, name: metricId }
            })
          }
        }

        setScopeData({ frameworkName, categories, cpis })
      } catch {
        setScopeData(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchScopeData()
  }, [isOpen, template])

  if (!template) return null

  const config = template.config
  const timeFrame = config?.timePeriod || (config?.dateFrom && config?.dateTo ? `${config.dateFrom} to ${config.dateTo}` : '-')
  const frequency = config?.frequency ? config.frequency.charAt(0).toUpperCase() + config.frequency.slice(1) : '-'

  return (
    <StyledDrawer anchor="right" open={isOpen} onClose={onClose}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{template.name}</DrawerTitle>
          <IconButton onClick={onClose} sx={{ color: theme.colors.devicesSecondaryText }}>
            <CloseIcon />
          </IconButton>
        </DrawerHeader>

        <DrawerDescription>{template.description || ''}</DrawerDescription>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* General Section */}
            <SectionHeading>General</SectionHeading>
            <Stack spacing={0}>
              <InfoRow>
                <InfoIconWrapper><DateCreatedIcon /></InfoIconWrapper>
                <Box>
                  <InfoLabel>Date created</InfoLabel>
                  <InfoValue>{formatDate(template.created_at)}</InfoValue>
                </Box>
              </InfoRow>
              <InfoRow>
                <InfoIconWrapper><DateEditedIcon /></InfoIconWrapper>
                <Box>
                  <InfoLabel>Date edited</InfoLabel>
                  <InfoValue>{formatDate(template.updated_at)}</InfoValue>
                </Box>
              </InfoRow>
              <InfoRow>
                <InfoIconWrapper><DateGeneratedIcon /></InfoIconWrapper>
                <Box>
                  <InfoLabel>Date generated</InfoLabel>
                  <InfoValue>{formatDate(template.last_generated)}</InfoValue>
                </Box>
              </InfoRow>
            </Stack>

            <SectionDivider />

            {/* Scope Section */}
            <SectionHeading>Scope</SectionHeading>
            <Stack spacing={0}>
              <InfoRow>
                <InfoIconWrapper><CubeTransparentIcon width={18} height={18} stroke="#A88CA4" /></InfoIconWrapper>
                <Box>
                  <InfoLabel>Framework</InfoLabel>
                  <InfoValue sx={{ textTransform: 'capitalize' }}>
                    {scopeData?.frameworkName || '-'}
                    {hasFrameworkError && (
                      <FrameworkRemovedTooltip
                        message="The framework used for this template is no longer available. You can use this template with your current framework."
                        onFrameworkChange={onFrameworkChange}
                      >
                        <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}>
                          <ExclamationCircleIcon width={16} height={16} color={theme.colorTokens.info.attention} />
                        </span>
                      </FrameworkRemovedTooltip>
                    )}
                  </InfoValue>
                </Box>
              </InfoRow>
              <InfoRow>
                <InfoIconWrapper><Squares2X2Icon width={18} height={18} stroke="#A88CA4" /></InfoIconWrapper>
                <Box>
                  {(!scopeData?.cpis.length || !!scopeData?.categories.length) && (
                    <InfoLabel>
                      Category: {scopeData?.categories.length
                        ? scopeData.categories[0].name
                        : 'All Categories'}
                      {scopeData?.categories[0] && invalidItemMap.has(scopeData.categories[0].id) && (
                        <OnyxiaTooltip title={`${scopeData.categories[0].name} is no longer available`} arrow>
                          <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}>
                            <ExclamationCircleIcon width={16} height={16} color={theme.colorTokens.info.attention} />
                          </span>
                        </OnyxiaTooltip>
                      )}
                      {scopeData && scopeData.categories.length > 1 && (
                        <OnyxiaTooltip title={scopeData.categories.slice(1).map((c) => c.name).join(', ')} arrow>
                          <span style={{ color: theme.colors.cta, cursor: 'pointer' }}>
                            {` +${scopeData.categories.length - 1}`}
                          </span>
                        </OnyxiaTooltip>
                      )}
                    </InfoLabel>
                  )}
                  {!!scopeData?.cpis.length && (
                    <InfoLabel>
                      CPI: {scopeData.cpis[0].name}
                      {scopeData.cpis[0] && invalidItemMap.has(scopeData.cpis[0].id) && (
                        <OnyxiaTooltip title={`${scopeData.cpis[0].name} is no longer available`} arrow>
                          <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}>
                            <ExclamationCircleIcon width={16} height={16} color={theme.colorTokens.info.attention} />
                          </span>
                        </OnyxiaTooltip>
                      )}
                      {scopeData.cpis.length > 1 && (
                        <OnyxiaTooltip title={scopeData.cpis.slice(1).map((c) => c.name).join(', ')} arrow>
                          <span style={{ color: theme.colors.cta, cursor: 'pointer' }}>
                            {` +${scopeData.cpis.length - 1}`}
                          </span>
                        </OnyxiaTooltip>
                      )}
                    </InfoLabel>
                  )}
                </Box>
              </InfoRow>
              <InfoRow>
                <InfoIconWrapper><ChartBarIcon width={18} height={18} stroke="#A88CA4" /></InfoIconWrapper>
                <Box>
                  <InfoLabel>CPI Appendix</InfoLabel>
                  <InfoValue>{config?.isIncludeCPIAppendix ? 'Included' : 'Not included'}</InfoValue>
                </Box>
              </InfoRow>
              <InfoRow>
                <InfoIconWrapper><TagIcon width={18} height={18} stroke="#A88CA4" /></InfoIconWrapper>
                <Box>
                  <InfoLabel>Public Labels</InfoLabel>
                  <InfoValue>{config?.isIncludePublicLabels ? 'Included' : 'Not included'}</InfoValue>
                </Box>
              </InfoRow>
            </Stack>

            <SectionDivider />

            {/* Time & Frequency Section */}
            <SectionHeading>Time & Frequency</SectionHeading>
            <Stack spacing={0}>
              <InfoRow>
                <InfoIconWrapper><ClockIcon width={18} height={18} stroke="#A88CA4" /></InfoIconWrapper>
                <Box>
                  <InfoLabel>Time frame</InfoLabel>
                  <InfoValue sx={{ textTransform: 'uppercase' }}>{timeFrame}</InfoValue>
                </Box>
              </InfoRow>
              <InfoRow>
                <InfoIconWrapper><ArrowPathIcon width={18} height={18} stroke="#A88CA4" /></InfoIconWrapper>
                <Box>
                  <InfoLabel>Frequency</InfoLabel>
                  <InfoValue>{frequency}</InfoValue>
                </Box>
              </InfoRow>
            </Stack>

            {template.schedule && (
              <>
                <SectionDivider />
                <SectionHeading>Schedule</SectionHeading>
                <Stack spacing={0}>
                  <InfoRow>
                    <InfoIconWrapper><CalendarIcon width={18} height={18} stroke="#A88CA4" /></InfoIconWrapper>
                    <Box>
                      <InfoLabel>Start date</InfoLabel>
                      <InfoValue>{formatScheduleDate(template.schedule.start_date)}</InfoValue>
                    </Box>
                  </InfoRow>
                  <InfoRow>
                    <InfoIconWrapper><ArrowPathIcon width={18} height={18} stroke="#A88CA4" /></InfoIconWrapper>
                    <Box>
                      <InfoLabel>Repeat every</InfoLabel>
                      <InfoValue sx={{ textTransform: 'capitalize' }}>
                        {formatScheduleFrequency(template.schedule.interval)}
                      </InfoValue>
                    </Box>
                  </InfoRow>
                  <InfoRow>
                    <InfoIconWrapper><UsersIcon width={18} height={18} stroke="#A88CA4" /></InfoIconWrapper>
                    <Box>
                      <InfoLabel>Recipients</InfoLabel>
                      <InfoValue>
                        {(() => {
                          const ids = template.schedule.recipients
                          if (ids.length === 0) return '-'
                          // A recipient the app cannot name is left out rather than shown as a bare id.
                          const labels = ids
                            .map((id) => (getUser(id) ? getUserLabel(id) : null))
                            .filter((label): label is string => !!label)
                          if (labels.length === 0) return ''
                          const firstLabel = labels[0]
                          const remaining = labels.length - 1
                          if (remaining === 0) return firstLabel
                          const tooltipText = labels.slice(1).join(', ')
                          return (
                            <>
                              {firstLabel}
                              <OnyxiaTooltip title={tooltipText} arrow>
                                <span style={{ color: theme.colors.cta, cursor: 'pointer', marginLeft: 4 }}>
                                  +{remaining}
                                </span>
                              </OnyxiaTooltip>
                            </>
                          )
                        })()}
                      </InfoValue>
                    </Box>
                  </InfoRow>
                </Stack>
              </>
            )}
          </>
        )}
      </DrawerContent>
    </StyledDrawer>
  )
}

export default ViewTemplateDrawer

const DateCreatedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M5.08278 2.5V4.03947M12.3146 2.5V4.03947M2.5 13.2763V5.57895C2.5 4.72872 3.19381 4.03947 4.04967 4.03947H13.3477C14.2035 4.03947 14.8974 4.72872 14.8974 5.57895V8.14474M2.5 13.2763C2.5 14.1265 3.19381 14.8158 4.04967 14.8158H9.38742M2.5 13.2763V8.14474C2.5 7.29451 3.19381 6.60526 4.04967 6.60526H13.3477C14.2035 6.60526 14.8974 7.29451 14.8974 8.14474M14.8974 8.14474V9.34211M10.851 13.1908H13.1755M13.1755 13.1908H15.5M13.1755 13.1908V10.8816M13.1755 13.1908V15.5" stroke="#A88CA4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const DateEditedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3.06579 0.5V2.03947M10.25 0.5V2.03947M0.5 11.2763V3.57895C0.5 2.72872 1.18925 2.03947 2.03947 2.03947H11.2763C12.1265 2.03947 12.8158 2.72872 12.8158 3.57895V6.14474M0.5 11.2763C0.5 12.1265 1.18925 12.8158 2.03947 12.8158H7.3421M0.5 11.2763V6.14474C0.5 5.29451 1.18925 4.60526 2.03947 4.60526H11.2763C12.1265 4.60526 12.8158 5.29451 12.8158 6.14474M12.8158 6.14474V7.3421" stroke="#A88CA4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M13.3251 8.88629C13.0913 8.65249 12.7122 8.65249 12.4784 8.88629L12.2145 9.1502L13.0611 9.99687L13.3251 9.73295C13.5589 9.49915 13.5589 9.12009 13.3251 8.88629Z" fill="currentColor" />
    <path d="M12.8192 10.2388L11.9726 9.39211L9.20134 12.1633C9.06067 12.304 8.95726 12.4775 8.90047 12.6682L8.71806 13.2805C8.70013 13.3407 8.71663 13.4059 8.76104 13.4503C8.80545 13.4947 8.87063 13.5112 8.93083 13.4933L9.54317 13.3109C9.73383 13.2541 9.90733 13.1507 10.048 13.01L12.8192 10.2388Z" fill="currentColor" />
  </svg>
)

const DateGeneratedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M4.69231 2.5V4.06M12.2308 2.5V4.06M2 13.42V5.62C2 4.75844 2.72323 4.06 3.61538 4.06H13.3077C14.1998 4.06 14.9231 4.75844 14.9231 5.62V8.22M2 13.42C2 14.2816 2.72323 14.98 3.61538 14.98H9.17949M2 13.42V8.22C2 7.35844 2.72323 6.66 3.61538 6.66H13.3077C14.1998 6.66 14.9231 7.35844 14.9231 8.22M14.9231 8.22V9.43333M11.6923 14.46V14.98C11.6923 15.2672 11.9334 15.5 12.2308 15.5H15.4615C15.7589 15.5 16 15.2672 16 14.98V14.46M12.7692 12.38L13.8462 11.34M13.8462 11.34L14.9231 12.38M13.8462 11.34V14.46" stroke="#A88CA4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

